import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Tooltip } from 'antd';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { useHistory } from 'react-router-dom';

import CheckAccess from 'navigation/CheckAccess';
import FormCheckbox from 'components/shared/BWFormControl/FormCheckbox';
import FormDateRange from 'components/shared/BWFormControl/FormDateRange';
import FormInput from 'components/shared/BWFormControl/FormInput';
import FormSelect from 'components/shared/BWFormControl/FormSelect';
import useGetOptions, { optionType } from 'hooks/useGetOptions';
import { exportAccountingReportPdf, getAccountingReport } from 'services/report';
import { cdnPath } from 'utils';
import { showToast } from 'utils/helpers';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import {
  buildTrialBalanceTree,
  buildTrialBalanceExportRows,
  getBalanceChecks,
  getTrialBalancePeriod,
  getTrialBalanceScale,
  getVisibleTrialBalanceRows,
  scaleTrialBalanceAmount,
  TRIAL_BALANCE_AMOUNT_FIELDS,
  validateTrialBalancePeriod,
} from './trialBalanceUtils';

const TREE_PAGE_SIZE = 100000;
const QUICK_PERIOD_OPTIONS = [
  { value: 'month', label: 'Tháng này' },
  { value: 'previous_month', label: 'Tháng trước' },
  { value: 'quarter', label: 'Quý này' },
  { value: 'previous_quarter', label: 'Quý trước' },
  { value: 'year', label: 'Năm nay' },
  { value: 'previous_year', label: 'Năm trước' },
  { value: 'custom', label: 'Tùy chọn' },
];
const LEVEL_OPTIONS = [
  { value: 0, label: 'Tất cả các bậc' },
  { value: 1, label: 'Bậc 1' },
  { value: 2, label: 'Đến bậc 2' },
  { value: 3, label: 'Đến bậc 3' },
  { value: 4, label: 'Đến bậc 4' },
];
const UNIT_OPTIONS = [
  { value: 'unit', label: 'Đồng' },
  { value: 'thousand', label: 'Nghìn đồng' },
  { value: 'million', label: 'Triệu đồng' },
];
const DECIMAL_OPTIONS = [
  { value: 'company', label: 'Theo cấu hình' },
  { value: 0, label: '0 chữ số' },
  { value: 1, label: '1 chữ số' },
  { value: 2, label: '2 chữ số' },
  { value: 3, label: '3 chữ số' },
];
const TEMPLATE_STORAGE_KEY = 'baochau.gl005.saved-templates';
const COLUMN_STORAGE_KEY = 'baochau.gl005.visible-columns';

const REPORT_COLUMNS = [
  { key: 'account', label: 'Tài khoản', always: true },
  { key: 'opening', label: 'Số dư đầu kỳ', fields: ['OPENINGDEBIT', 'OPENINGCREDIT'] },
  { key: 'ytd', label: 'Lũy kế từ đầu năm', fields: ['YTDDEBIT', 'YTDCREDIT'] },
  { key: 'movement', label: 'Phát sinh trong kỳ', fields: ['DEBITAMOUNT', 'CREDITAMOUNT'] },
  { key: 'closing', label: 'Số dư cuối kỳ', fields: ['CLOSINGDEBIT', 'CLOSINGCREDIT'] },
];

const toScopeSelection = (id, label) =>
  id ? [{ id, value: id, label: label || String(id) }] : [];

const unwrapReport = (response = {}) => {
  const payload =
    response?.data && !Array.isArray(response.data) && (Array.isArray(response.data.data) || response.data.summary)
      ? response.data
      : response;
  const items = Array.isArray(payload.data) ? payload.data : Array.isArray(payload.items) ? payload.items : [];
  const limit = Number(payload.limit || 50);
  const total = Number(payload.total || payload.summary?.TOTALITEMS || 0);
  return {
    items,
    summary: payload.summary || {},
    page: Number(payload.page || 1),
    limit,
    total,
    totalPages: Math.max(1, Math.ceil(total / limit)),
  };
};

const serializeSelection = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => item?.value ?? item?.id ?? item)
    .filter((item) => item !== null && item !== undefined && item !== '')
    .join(',') || null;

const serializeParams = (values = {}, page = 1, itemsPerPage = TREE_PAGE_SIZE) => ({
  ...values,
  business_ids: serializeSelection(values.business_ids),
  store_ids: serializeSelection(values.store_ids),
  accounting_account_codes: serializeSelection(values.accounting_account_codes),
  include_zero: Number(values.include_zero || 0),
  include_children: Number(values.include_children || 0),
  include_inactive: Number(values.include_inactive || 0),
  show_two_sided: Number(values.show_two_sided || 0),
  show_ytd: Number(values.show_ytd || 0),
  show_parent_code: Number(values.show_parent_code || 0),
  page,
  itemsPerPage,
});

const readStoredJson = (key, fallback) => {
  try {
    return JSON.parse(window.localStorage.getItem(key)) || fallback;
  } catch (error) {
    return fallback;
  }
};

const formatRawAmount = (value, maximumFractionDigits = 4) =>
  new Intl.NumberFormat('vi-VN', {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(Number(value || 0));

const countSerializedSelections = (value) =>
  `${value || ''}`
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean).length;

const formatFilterDate = (value) => {
  const date = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
  return value && date.isValid() ? date.format('DD/MM/YYYY') : '—';
};

const TrialBalanceReport = ({ report }) => {
  const history = useHistory();
  const initialScope = useMemo(() => getPortalScope(), []);
  const defaultPeriod = useMemo(() => getTrialBalancePeriod('month'), []);
  const methods = useForm({
    defaultValues: {
      company_id: initialScope.company_id || null,
      business_ids: toScopeSelection(initialScope.business_id, initialScope.business_name),
      store_ids: toScopeSelection(initialScope.store_id, initialScope.store_name),
      quick_period: 'month',
      accounting_date_from: defaultPeriod.from,
      accounting_date_to: defaultPeriod.to,
      accounting_account_code: '',
      accounting_account_code_to: '',
      accounting_account_codes: [],
      keyword: '',
      account_level: 0,
      include_zero: 0,
      include_children: 1,
      include_inactive: 1,
      show_two_sided: 1,
      show_ytd: 0,
      show_parent_code: 0,
      display_unit: 'unit',
      decimal_places: 'company',
    },
  });
  const selectedCompanyId = methods.watch('company_id');
  const showYtd = Boolean(methods.watch('show_ytd'));
  const companyOptions = useGetOptions(optionType.company);
  const businessOptionParams = useMemo(
    () => ({ is_active: 1, ...(selectedCompanyId ? { company_id: selectedCompanyId } : {}) }),
    [selectedCompanyId],
  );
  const storeOptionParams = useMemo(
    () => ({ is_active: 1, ...(selectedCompanyId ? { company_id: selectedCompanyId } : {}) }),
    [selectedCompanyId],
  );
  const accountOptionParams = useMemo(
    () => ({ is_active: 2, limit: 5000, ...(selectedCompanyId ? { company_id: selectedCompanyId } : {}) }),
    [selectedCompanyId],
  );
  const businessOptions = useGetOptions(optionType.business, { params: businessOptionParams });
  const storeOptions = useGetOptions(optionType.store, { params: storeOptionParams });
  const rawAccountOptions = useGetOptions(optionType.accountingAccount, {
    params: accountOptionParams,
    valueName: 'code',
    labelName: 'accountingaccountname',
    valueAsString: true,
  });
  const accountOptions = useMemo(
    () => rawAccountOptions.map((item) => ({
      ...item,
      label: `${item.code || item.value} - ${item.accountingaccountname || item.name || ''}`.replace(/\s+-\s+$/, ''),
    })),
    [rawAccountOptions],
  );
  const [query, setQuery] = useState(() => serializeParams(methods.getValues()));
  const [data, setData] = useState({ items: [], summary: {}, page: 1, limit: TREE_PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const reportRequestIdRef = useRef(0);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [columnPanelOpen, setColumnPanelOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState(() => readStoredJson(TEMPLATE_STORAGE_KEY, []));
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [collapsedAccountCodes, setCollapsedAccountCodes] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(() =>
    readStoredJson(COLUMN_STORAGE_KEY, ['account', 'opening', 'movement', 'closing']),
  );
  const displayUnit = methods.watch('display_unit');
  const decimalSetting = methods.watch('decimal_places');
  const showParentCode = methods.watch('show_parent_code');
  const decimals = decimalSetting === 'company' ? 0 : Number(decimalSetting || 0);
  const unitMeta = getTrialBalanceScale(displayUnit);

  const loadData = useCallback(async () => {
    const requestId = ++reportRequestIdRef.current;
    if (!query.company_id) {
      setLoading(false);
      setData((current) => ({ ...current, items: [], summary: {}, total: 0, totalPages: 1 }));
      return;
    }
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await getAccountingReport(report.code, query);
      if (requestId !== reportRequestIdRef.current) return;
      setData(unwrapReport(response));
      setLastUpdated(moment());
    } catch (error) {
      if (requestId !== reportRequestIdRef.current) return;
      const message = error?.message || 'Không tải được Bảng cân đối số phát sinh.';
      setErrorMessage(message);
      showToast.error(message);
    } finally {
      if (requestId === reportRequestIdRef.current) setLoading(false);
    }
  }, [query, report.code]);

  useEffect(() => {
    loadData();
    return () => { reportRequestIdRef.current += 1; };
  }, [loadData]);

  useEffect(() => {
    const onScopeChange = (event) => {
      const scope = event?.detail || getPortalScope();
      methods.setValue('company_id', scope.company_id || null);
      methods.setValue('business_ids', toScopeSelection(scope.business_id, scope.business_name));
      methods.setValue('store_ids', toScopeSelection(scope.store_id, scope.store_name));
      setQuery((current) => {
        const next = {
          ...current,
          company_id: scope.company_id || null,
          business_ids: scope.business_id || null,
          store_ids: scope.store_id || null,
          page: 1,
        };
        return ['company_id', 'business_ids', 'store_ids'].every(
          (field) => String(current[field] || '') === String(next[field] || ''),
        ) ? current : next;
      });
    };
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, onScopeChange);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, onScopeChange);
  }, [methods]);

  useEffect(() => {
    if (selectedCompanyId || companyOptions.length !== 1) return;
    const companyId = companyOptions[0]?.value ?? companyOptions[0]?.id;
    if (!companyId) return;
    methods.setValue('company_id', companyId);
    setQuery((current) => ({ ...current, company_id: companyId, page: 1 }));
  }, [companyOptions, methods, selectedCompanyId]);

  const submitFilters = methods.handleSubmit((values) => {
    if (!values.company_id) {
      showToast.error('Vui lòng chọn công ty.');
      return;
    }
    const periodError = validateTrialBalancePeriod(values.accounting_date_from, values.accounting_date_to);
    if (periodError) {
      showToast.error(periodError);
      return;
    }
    setQuery(serializeParams(values, 1, TREE_PAGE_SIZE));
  });

  const resetFilters = () => {
    const scope = getPortalScope();
    const period = getTrialBalancePeriod('month');
    const values = {
      ...methods.getValues(),
      company_id: scope.company_id || null,
      business_ids: toScopeSelection(scope.business_id, scope.business_name),
      store_ids: toScopeSelection(scope.store_id, scope.store_name),
      quick_period: 'month',
      accounting_date_from: period.from,
      accounting_date_to: period.to,
      accounting_account_code: '',
      accounting_account_code_to: '',
      accounting_account_codes: [],
      keyword: '',
      account_level: 0,
      include_zero: 0,
      include_children: 1,
      include_inactive: 1,
      show_two_sided: 1,
      show_ytd: 0,
      show_parent_code: 0,
      display_unit: 'unit',
      decimal_places: 'company',
    };
    methods.reset(values);
    setCollapsedAccountCodes([]);
    setQuery(serializeParams(values, 1, TREE_PAGE_SIZE));
  };

  const changeQuickPeriod = (value) => {
    if (value === 'custom') return;
    const period = getTrialBalancePeriod(value);
    methods.setValue('accounting_date_from', period.from);
    methods.setValue('accounting_date_to', period.to);
  };

  const visibleGroups = useMemo(
    () => REPORT_COLUMNS.filter((column) =>
      column.always || (column.key === 'ytd' ? showYtd : visibleColumns.includes(column.key))),
    [showYtd, visibleColumns],
  );
  const visibleAmountFields = useMemo(
    () => visibleGroups.flatMap((group) => group.fields || []).filter((field) => TRIAL_BALANCE_AMOUNT_FIELDS.includes(field)),
    [visibleGroups],
  );
  const checks = useMemo(() => getBalanceChecks(data.summary), [data.summary]);
  const isMovementBalanced = Math.abs(checks.movementDifference) < 0.0001;
  const isClosingBalanced = Math.abs(checks.closingEquationDifference) < 0.0001;
  const companyLabel =
    companyOptions.find((option) => String(option.value) === String(selectedCompanyId))?.label ||
    initialScope.company_name ||
    'Theo phạm vi được cấp';
  const appliedFilterBadges = useMemo(() => {
    const businessCount = countSerializedSelections(query.business_ids);
    const storeCount = countSerializedSelections(query.store_ids);
    const badges = [
      {
        key: 'period',
        icon: 'fi fi-rr-calendar',
        text: `${formatFilterDate(query.accounting_date_from)} – ${formatFilterDate(query.accounting_date_to)}`,
      },
      {
        key: 'business',
        icon: 'fi fi-rr-building',
        text: businessCount ? `${businessCount} chi nhánh` : 'Tất cả chi nhánh',
      },
      {
        key: 'store',
        icon: 'fi fi-rr-shop',
        text: storeCount ? `${storeCount} cửa hàng` : 'Tất cả cửa hàng',
      },
    ];

    if (query.accounting_account_code || query.accounting_account_code_to) {
      badges.push({
        key: 'account-range',
        icon: 'fi fi-rr-book-alt',
        text: `TK ${query.accounting_account_code || 'đầu'} → ${query.accounting_account_code_to || 'cuối'}`,
      });
    }
    if (query.keyword) {
      badges.push({ key: 'keyword', icon: 'fi fi-rr-search', text: `Tìm: ${query.keyword}` });
    }
    if (Number(query.show_two_sided) === 1) {
      badges.push({
        key: 'two-sided',
        icon: 'fi fi-rr-users',
        text: 'Số dư hai bên theo đối tượng',
      });
    }
    return badges;
  }, [query]);

  const formatDisplayAmount = useCallback(
    (value) =>
      new Intl.NumberFormat('vi-VN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(scaleTrialBalanceAmount(value, displayUnit)),
    [decimals, displayUnit],
  );

  const renderAmount = (value) => {
    const numericValue = Number(value || 0);
    return (
      <Tooltip title={`${formatRawAmount(numericValue)} VND`}>
        <span className={numericValue < 0 ? 'bc_tb_negative' : ''}>
          {numericValue < 0 ? `(${formatDisplayAmount(Math.abs(numericValue))})` : formatDisplayAmount(numericValue)}
        </span>
      </Tooltip>
    );
  };

  const fetchAllRows = useCallback(async () => {
    const response = await getAccountingReport(report.code, { ...query, page: 1, itemsPerPage: 100000 });
    return unwrapReport(response);
  }, [query, report.code]);

  const exportExcel = async () => {
    setLoadingExport(true);
    try {
      const fullData = await fetchAllRows();
      const rows = buildTrialBalanceExportRows(fullData.items);
      rows.push({
        'Mã tài khoản': 'TỔNG CỘNG',
        'Tên tài khoản': '',
        'Tài khoản cha': '',
        Bậc: '',
        'Dư Nợ đầu kỳ': Number(fullData.summary.OPENINGDEBIT || 0),
        'Dư Có đầu kỳ': Number(fullData.summary.OPENINGCREDIT || 0),
        'Phát sinh Nợ': Number(fullData.summary.DEBITAMOUNT || 0),
        'Phát sinh Có': Number(fullData.summary.CREDITAMOUNT || 0),
        'Lũy kế Nợ đầu năm': Number(fullData.summary.YTDDEBIT || 0),
        'Lũy kế Có đầu năm': Number(fullData.summary.YTDCREDIT || 0),
        'Dư Nợ cuối kỳ': Number(fullData.summary.CLOSINGDEBIT || 0),
        'Dư Có cuối kỳ': Number(fullData.summary.CLOSINGCREDIT || 0),
      });
      const worksheet = XLSX.utils.aoa_to_sheet([
        [report.name],
        [`Mã báo cáo: ${report.code}`],
        [`Công ty: ${companyLabel}`],
        [`Kỳ báo cáo: ${query.accounting_date_from || ''} - ${query.accounting_date_to || ''}`],
        [`Đơn vị: ${unitMeta.label}`],
        [],
      ]);
      XLSX.utils.sheet_add_json(worksheet, rows, { origin: 'A7' });
      worksheet['!cols'] = [{ wch: 16 }, { wch: 42 }, { wch: 16 }, { wch: 8 }, ...Array(8).fill({ wch: 19 })];
      worksheet['!merges'] = [XLSX.utils.decode_range('A1:L1')];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, report.code);
      XLSX.writeFile(workbook, `${report.code}_${moment().format('YYYYMMDD_HHmm')}.xlsx`);
    } catch (error) {
      showToast.error(error?.message || 'Không xuất được Excel.');
    } finally {
      setLoadingExport(false);
    }
  };

  const exportPdf = async () => {
    setLoadingPdf(true);
    try {
      const response = await exportAccountingReportPdf(report.code, {
        ...query,
        display_unit: displayUnit,
        decimal_places: decimals,
        company_name: companyLabel,
      });
      const path = response?.data?.path || response?.path;
      if (!path) throw new Error('Máy chủ không trả về file PDF.');
      window.open(cdnPath(path), '_blank', 'noopener,noreferrer');
    } catch (error) {
      showToast.error(error?.message || 'Không xuất được PDF.');
    } finally {
      setLoadingPdf(false);
    }
  };

  const printReport = () => {
    document.body.classList.add('bc_trial_balance_printing');
    window.setTimeout(() => {
      window.print();
      document.body.classList.remove('bc_trial_balance_printing');
    }, 50);
  };

  const saveTemplate = () => {
    const name = window.prompt('Tên mẫu báo cáo');
    if (!name?.trim()) return;
    const nextTemplates = [
      ...savedTemplates.filter((item) => item.name !== name.trim()),
      { name: name.trim(), values: methods.getValues() },
    ];
    window.localStorage.setItem(TEMPLATE_STORAGE_KEY, JSON.stringify(nextTemplates));
    setSavedTemplates(nextTemplates);
    setSelectedTemplate(name.trim());
    showToast.success('Đã lưu mẫu báo cáo cá nhân.');
  };

  const applyTemplate = (name) => {
    setSelectedTemplate(name);
    const template = savedTemplates.find((item) => item.name === name);
    if (!template) return;
    methods.reset({ ...methods.getValues(), ...template.values });
    setQuery(serializeParams({ ...methods.getValues(), ...template.values }, 1, TREE_PAGE_SIZE));
  };

  const deleteTemplate = () => {
    if (!selectedTemplate) return;
    if (!window.confirm(`Xóa mẫu báo cáo "${selectedTemplate}"?`)) return;
    const nextTemplates = savedTemplates.filter((item) => item.name !== selectedTemplate);
    window.localStorage.setItem(TEMPLATE_STORAGE_KEY, JSON.stringify(nextTemplates));
    setSavedTemplates(nextTemplates);
    setSelectedTemplate('');
  };

  const toggleColumn = (key) => {
    const next = visibleColumns.includes(key)
      ? visibleColumns.filter((columnKey) => columnKey !== key)
      : [...visibleColumns, key];
    setVisibleColumns(next);
    window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(next));
  };

  const openAccountDetail = (row) => {
    const params = new URLSearchParams({
      accounting_account_code: row.ACCOUNTINGACCOUNTCODE,
      accounting_date_from: query.accounting_date_from || '',
      accounting_date_to: query.accounting_date_to || '',
      include_children: row.HASCHILDREN ? '1' : '0',
    });
    history.push(`/accounting-reports/gl-001?${params.toString()}`);
  };

  const treeRows = useMemo(() => buildTrialBalanceTree(data.items), [data.items]);
  const accountCodesWithChildren = useMemo(
    () => new Set(treeRows.filter((row) => row.HASCHILDREN).map((row) => row.ACCOUNTINGACCOUNTCODE)),
    [treeRows],
  );
  const visibleRows = useMemo(
    () => getVisibleTrialBalanceRows(data.items, collapsedAccountCodes),
    [collapsedAccountCodes, data.items],
  );

  const toggleAccount = (accountCode) => {
    setCollapsedAccountCodes((current) =>
      current.includes(accountCode) ? current.filter((code) => code !== accountCode) : [...current, accountCode],
    );
  };

  return (
    <FormProvider {...methods}>
      <div className='bw_main_wrapp bc_accounting_reports bc_trial_balance'>
        <section className='bc_tb_header'>
          <div className='bc_tb_heading'>
            <div className='bc_tb_breadcrumb'>Báo cáo kế toán ERP / Sổ kế toán</div>
            <div className='bc_tb_title_row'>
              <h1>{report.name}</h1>
              <span className='bc_tb_report_code'>{report.code}</span>
            </div>
            <div className='bc_tb_meta'>
              <span><i className='fi fi-rr-building' /> {companyLabel}</span>
              <span><i className='fi fi-rr-calendar' /> {query.accounting_date_from || '—'} đến {query.accounting_date_to || '—'}</span>
              <span><i className='fi fi-rr-coins' /> VND · {unitMeta.label}</span>
              <span><i className='fi fi-rr-clock' /> Cập nhật {lastUpdated ? lastUpdated.format('HH:mm DD/MM/YYYY') : '—'}</span>
            </div>
          </div>
          <div className='bc_tb_toolbar'>
            <button type='button' className='bc_tb_btn bc_tb_btn_primary' onClick={submitFilters}>
              <i className='fi fi-rr-play' /> Xem báo cáo
            </button>
            <button type='button' className='bc_tb_btn' onClick={loadData}>
              <i className='fi fi-rr-refresh' /> Làm mới
            </button>
            <button type='button' className='bc_tb_btn' onClick={saveTemplate}>
              <i className='fi fi-rr-disk' /> Lưu mẫu
            </button>
            <button type='button' className='bc_tb_btn' onClick={() => setColumnPanelOpen((open) => !open)}>
              <i className='fi fi-rr-settings-sliders' /> Tùy chỉnh cột
            </button>
            <CheckAccess permission={['AC_TRIAL_BALANCE_EXPORT', 'RP_ACCOUNTING_REPORT_VIEW']} any>
              <button type='button' className='bc_tb_btn' disabled={loadingExport} onClick={exportExcel}>
                <i className='fi fi-rr-file-spreadsheet' /> Excel
              </button>
              <button type='button' className='bc_tb_btn' disabled={loadingPdf} onClick={exportPdf}>
                <i className='fi fi-rr-file-pdf' /> PDF
              </button>
              <button type='button' className='bc_tb_btn' onClick={printReport}>
                <i className='fi fi-rr-print' /> In
              </button>
            </CheckAccess>
          </div>
        </section>

        {columnPanelOpen && (
          <section className='bc_tb_column_panel'>
            <strong>Cột hiển thị</strong>
            {REPORT_COLUMNS.filter((column) => !column.always && column.key !== 'ytd').map((column) => (
              <label key={column.key}>
                <input type='checkbox' checked={visibleColumns.includes(column.key)} onChange={() => toggleColumn(column.key)} />
                {column.label}
              </label>
            ))}
            <button
              type='button'
              onClick={() => {
                const defaults = ['account', 'opening', 'movement', 'closing'];
                setVisibleColumns(defaults);
                window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(defaults));
              }}>
              Khôi phục mặc định
            </button>
          </section>
        )}

        <section className={`bc_tb_filters ${filtersOpen ? 'is-open' : 'is-collapsed'}`}>
          <div className='bc_tb_filter_summary'>
            <button
              type='button'
              className='bc_tb_filter_toggle'
              aria-expanded={filtersOpen}
              aria-controls='bc-trial-balance-filter-panel'
              onClick={() => setFiltersOpen((open) => !open)}>
              <i className='fi fi-rr-filter' />
              <span>{filtersOpen ? 'Ẩn bộ lọc' : 'Mở bộ lọc'}</span>
              <i className={`fi fi-rr-angle-${filtersOpen ? 'up' : 'down'}`} />
            </button>
            <div className='bc_tb_filter_badges' role='group' aria-label='Điều kiện lọc đang áp dụng'>
              {appliedFilterBadges.map((item) => (
                <span key={item.key} title={item.text}>
                  <i className={item.icon} /> {item.text}
                </span>
              ))}
            </div>
            <div className='bc_tb_filter_actions'>
              <button type='button' className='bc_tb_btn' onClick={resetFilters}>Đặt lại</button>
              <button type='button' className='bc_tb_btn bc_tb_btn_primary' onClick={submitFilters}>Xem báo cáo</button>
            </div>
          </div>

          {filtersOpen && (
            <div id='bc-trial-balance-filter-panel' className='bc_tb_filter_panel'>
              <div className='bc_tb_filter_grid'>
                <label>
                  <span>Công ty *</span>
                  <FormSelect
                    field='company_id'
                    list={companyOptions}
                    allowClear={false}
                    disabled={companyOptions.length === 1}
                    onChange={() => {
                      methods.setValue('business_ids', []);
                      methods.setValue('store_ids', []);
                      methods.setValue('accounting_account_code', '');
                      methods.setValue('accounting_account_code_to', '');
                      methods.setValue('accounting_account_codes', []);
                    }}
                  />
                </label>
                <label>
                  <span>Chi nhánh</span>
                  <FormSelect
                    field='business_ids'
                    list={businessOptions}
                    mode='multiple'
                    labelInValue
                    placeholder='Tất cả chi nhánh được cấp quyền'
                  />
                </label>
                <label>
                  <span>Cửa hàng</span>
                  <FormSelect
                    field='store_ids'
                    list={storeOptions}
                    mode='multiple'
                    labelInValue
                    placeholder='Tất cả cửa hàng được cấp quyền'
                  />
                </label>
                <label>
                  <span>Kỳ báo cáo</span>
                  <FormSelect field='quick_period' list={QUICK_PERIOD_OPTIONS} allowClear={false} onChange={changeQuickPeriod} />
                </label>
                <label className='bc_tb_date_filter'>
                  <span>Thời gian hạch toán *</span>
                  <FormDateRange
                    fieldStart='accounting_date_from'
                    fieldEnd='accounting_date_to'
                    placeholder={['Từ ngày', 'Đến ngày']}
                    format='DD/MM/YYYY'
                    allowClear
                    onCalendarChange={() => methods.setValue('quick_period', 'custom')}
                  />
                </label>
                <label className='bc_tb_keyword_filter'>
                  <span>Tìm tài khoản</span>
                  <FormInput field='keyword' placeholder='Mã hoặc tên tài khoản' />
                </label>
              </div>
              <div className='bc_tb_filter_footer'>
                <button
                  type='button'
                  className='bc_tb_link'
                  aria-expanded={advancedOpen}
                  aria-controls='bc-trial-balance-advanced-filter'
                  onClick={() => setAdvancedOpen((open) => !open)}>
                  <i className={`fi fi-rr-angle-${advancedOpen ? 'up' : 'down'}`} />
                  Bộ lọc nâng cao
                </button>
              </div>
              {advancedOpen && (
                <div id='bc-trial-balance-advanced-filter' className='bc_tb_advanced'>
                  <label>
                    <span>Tài khoản từ</span>
                    <FormSelect
                      field='accounting_account_code'
                      list={accountOptions}
                      showSearch
                      allowClear
                      placeholder='Tìm theo mã hoặc tên tài khoản'
                      filterOption={(input, option) => `${option?.label || ''}`.toLowerCase().includes(input.toLowerCase())}
                    />
                  </label>
                  <label>
                    <span>Tài khoản đến</span>
                    <FormSelect
                      field='accounting_account_code_to'
                      list={accountOptions}
                      showSearch
                      allowClear
                      placeholder='Tìm theo mã hoặc tên tài khoản'
                      filterOption={(input, option) => `${option?.label || ''}`.toLowerCase().includes(input.toLowerCase())}
                    />
                  </label>
                  <label>
                    <span>Bậc tài khoản</span>
                    <FormSelect field='account_level' list={LEVEL_OPTIONS} allowClear={false} />
                  </label>
                  <label>
                    <span>Đơn vị hiển thị</span>
                    <FormSelect field='display_unit' list={UNIT_OPTIONS} allowClear={false} />
                  </label>
                  <label>
                    <span>Số chữ số thập phân</span>
                    <FormSelect field='decimal_places' list={DECIMAL_OPTIONS} allowClear={false} />
                  </label>
                  <label className='bc_tb_account_list_filter'>
                    <span>Danh sách tài khoản</span>
                    <FormSelect
                      field='accounting_account_codes'
                      list={accountOptions}
                      mode='multiple'
                      showSearch
                      allowClear
                      placeholder='Chọn một hoặc nhiều tài khoản'
                      filterOption={(input, option) => `${option?.label || ''}`.toLowerCase().includes(input.toLowerCase())}
                    />
                  </label>
                  <div className='bc_tb_switches'>
                    <FormCheckbox field='include_zero' label='Hiển thị tài khoản không có số liệu' />
                    <FormCheckbox field='include_children' label='Hiển thị chi tiết tài khoản con' />
                    <FormCheckbox field='include_inactive' label='Tài khoản ngừng dùng nhưng có phát sinh' />
                    <FormCheckbox field='show_two_sided' label='Hiển thị số dư hai bên theo đối tượng' />
                    <FormCheckbox field='show_ytd' label='Hiển thị lũy kế từ đầu năm' />
                    <FormCheckbox field='show_parent_code' label='Hiển thị mã tài khoản cha' />
                  </div>
                  <div className='bc_tb_templates'>
                    <span>Mẫu báo cáo cá nhân</span>
                    <select value={selectedTemplate} onChange={(event) => applyTemplate(event.target.value)}>
                      <option value=''>-- Chọn mẫu --</option>
                      {savedTemplates.map((template) => <option key={template.name} value={template.name}>{template.name}</option>)}
                    </select>
                    <button type='button' onClick={saveTemplate}>Lưu mẫu hiện tại</button>
                    <button type='button' disabled={!selectedTemplate} onClick={deleteTemplate}>Xóa mẫu</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        <section className={`bc_tb_table_card ${filtersOpen ? 'is-filters-open' : 'is-filters-collapsed'}`}>
          {Number(data.summary.PENDINGOPENINGSCOPES) > 0 && (
            <div role='alert' className='bw_mb_2'>
              Có {Number(data.summary.PENDINGOPENINGSCOPES)} bộ đầu kỳ chưa ghi nhận hết thay đổi trong phạm vi đang xem.
              {' '}Báo cáo sử dụng số đã ghi nhận gần nhất. Kiểm tra tại Nhập tồn đầu kỳ trước khi chốt sổ.
            </div>
          )}
          <div className='bc_tb_table_caption'>
            <div className='bc_tb_caption_main'>
              <div className='bc_tb_caption_title'>
                <strong>Chi tiết tài khoản</strong>
                <span>{data.total} tài khoản · Đơn vị: {unitMeta.label}</span>
              </div>
              <div className='bc_tb_inline_checks' role='group' aria-label='Trạng thái cân đối báo cáo'>
                <div
                  className={isMovementBalanced ? 'is-balanced' : 'is-warning'}
                  title='Đối chiếu tổng phát sinh Nợ và tổng phát sinh Có trong phạm vi lọc'>
                  <i className={`fi fi-rr-${isMovementBalanced ? 'check-circle' : 'triangle-warning'}`} />
                  <strong>{isMovementBalanced ? 'Phát sinh cân' : 'Chênh lệch phát sinh'}</strong>
                  {!isMovementBalanced && renderAmount(checks.movementDifference)}
                </div>
                <div
                  className={isClosingBalanced ? 'is-balanced' : 'is-warning'}
                  title='Đối chiếu phương trình số dư cuối kỳ trên toàn bộ dữ liệu theo phạm vi lọc'>
                  <i className={`fi fi-rr-${isClosingBalanced ? 'check-circle' : 'triangle-warning'}`} />
                  <strong>{isClosingBalanced ? 'Số dư khớp công thức' : 'Chênh lệch số dư'}</strong>
                  {!isClosingBalanced && renderAmount(checks.closingEquationDifference)}
                </div>
                {Math.abs(Number(data.summary.OPENINGDEBIT || 0) - Number(data.summary.OPENINGCREDIT || 0)) >= 0.01 && (
                  <div className='is-warning' title='Chênh lệch tổng Dư Nợ và Dư Có đầu kỳ trong phạm vi lọc'>
                    <strong>Đầu kỳ chưa cân</strong>
                    {renderAmount(Number(data.summary.OPENINGDEBIT || 0) - Number(data.summary.OPENINGCREDIT || 0))}
                  </div>
                )}
              </div>
            </div>
            <div className='bc_tb_table_actions'>
              <button type='button' onClick={() => setCollapsedAccountCodes([])}>Mở rộng cây</button>
              <button
                type='button'
                onClick={() => setCollapsedAccountCodes([...accountCodesWithChildren])}>
                Thu gọn cây
              </button>
              <span>Nhấp đúp để mở Sổ chi tiết</span>
            </div>
          </div>
          <div className='bc_tb_table_scroll'>
            <table className='bc_tb_table'>
              <thead>
                <tr>
                  <th rowSpan='2' className='bc_tb_sticky_account'>Mã tài khoản</th>
                  <th rowSpan='2' className='bc_tb_sticky_name'>Tên tài khoản</th>
                  {visibleGroups.filter((group) => group.fields).map((group) => (
                    <th key={group.key} colSpan={group.fields.length}>{group.label}</th>
                  ))}
                </tr>
                <tr>
                  {visibleAmountFields.map((field) => (
                    <th key={field}>{field.endsWith('DEBIT') || field === 'DEBITAMOUNT' ? 'Nợ' : 'Có'}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && Array.from({ length: 8 }).map((_, index) => (
                  <tr key={`loading-${index}`} className='bc_tb_loading_row'>
                    <td colSpan={2 + visibleAmountFields.length}><span /></td>
                  </tr>
                ))}
                {!loading && errorMessage && (
                  <tr><td colSpan={2 + visibleAmountFields.length} className='bc_tb_empty'>
                    <i className='fi fi-rr-triangle-warning' />
                    <strong>Không tải được dữ liệu</strong>
                    <span>{errorMessage}</span>
                    <button type='button' onClick={loadData}>Thử lại</button>
                  </td></tr>
                )}
                {!loading && !errorMessage && data.items.length === 0 && (
                  <tr><td colSpan={2 + visibleAmountFields.length} className='bc_tb_empty'>
                    <i className='fi fi-rr-document' />
                    <strong>Không có dữ liệu phù hợp</strong>
                    <span>Hãy kiểm tra kỳ báo cáo, phạm vi đơn vị hoặc bộ lọc tài khoản.</span>
                  </td></tr>
                )}
                {!loading && !errorMessage && visibleRows.map((row) => {
                  const isParentAccount = Boolean(row.HASCHILDREN);
                  const treeLevel = Math.max(1, Number(row.TREELEVEL || row.ACCOUNTLEVEL || 1));
                  return (
                  <tr
                    key={row.ACCOUNTINGACCOUNTID || row.ACCOUNTINGACCOUNTCODE}
                    className={[
                      Number(row.ISACTIVE) === 0 ? 'is-inactive' : '',
                      isParentAccount ? 'is-parent-account' : '',
                    ].filter(Boolean).join(' ')}
                    onDoubleClick={() => openAccountDetail(row)}>
                    <td className='bc_tb_sticky_account'>
                      <div
                        className='bc_tb_account_identity'
                        style={{ paddingLeft: `${Math.max(0, treeLevel - 1) * 18}px` }}>
                      {isParentAccount ? (
                        <button
                          type='button'
                          className='bc_tb_tree_toggle'
                          aria-label={`${collapsedAccountCodes.includes(row.ACCOUNTINGACCOUNTCODE) ? 'Mở rộng' : 'Thu gọn'} ${row.ACCOUNTINGACCOUNTCODE}`}
                          onClick={() => toggleAccount(row.ACCOUNTINGACCOUNTCODE)}>
                          <i className={`fi fi-rr-angle-${collapsedAccountCodes.includes(row.ACCOUNTINGACCOUNTCODE) ? 'right' : 'down'}`} />
                        </button>
                      ) : (
                        <span className='bc_tb_tree_spacer' aria-hidden='true' />
                      )}
                      <button type='button' className='bc_tb_account_link' onClick={() => openAccountDetail(row)}>
                        {row.ACCOUNTINGACCOUNTCODE}
                      </button>
                      </div>
                    </td>
                    <td className='bc_tb_sticky_name'>
                      <span>
                        {row.ACCOUNTINGACCOUNTNAME}
                        {Number(row.ISACTIVE) === 0 && <small>Ngừng sử dụng</small>}
                        {Boolean(showParentCode) && Boolean(row.PARENTACCOUNTCODE) && <em>TK cha: {row.PARENTACCOUNTCODE}</em>}
                        {isParentAccount && (Number(row.DIRECTDEBITAMOUNT) !== 0 || Number(row.DIRECTCREDITAMOUNT) !== 0) &&
                          row.DIRECTDEBITAMOUNT != null && row.DIRECTCREDITAMOUNT != null && (
                          <em>
                            Ghi trực tiếp TK {row.ACCOUNTINGACCOUNTCODE}: Nợ {renderAmount(row.DIRECTDEBITAMOUNT)} · Có {renderAmount(row.DIRECTCREDITAMOUNT)}
                          </em>
                        )}
                      </span>
                    </td>
                    {visibleAmountFields.map((field) => <td key={field}>{renderAmount(row[field])}</td>)}
                  </tr>
                  );
                })}
              </tbody>
              {!loading && !errorMessage && data.items.length > 0 && (
                <tfoot>
                  <tr>
                    <th className='bc_tb_sticky_account' colSpan='2'>TỔNG CỘNG</th>
                    {visibleAmountFields.map((field) => <th key={field}>{renderAmount(data.summary[field])}</th>)}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <div className='bc_tb_paging'>
            <span>Hiển thị {visibleRows.length} / {data.total} tài khoản</span>
            <span>Tổng hợp toàn bộ phạm vi lọc · Cây tài khoản được tải đầy đủ cả tài khoản cha và con.</span>
          </div>
        </section>
      </div>
    </FormProvider>
  );
};

export default TrialBalanceReport;
